/**
 * 통합검색(GlobalSearch ⌘K) 조회 훅 (TanStack Query)
 *
 * - GET /search?q=   전 모듈 교차검색 (문서·CAPA·부적합·공급자 등 items 교차)  (계약 §1.3 범주)
 *
 * 동적 메타모델 원칙(절대):
 * - 모듈명 분기 금지. 결과는 범용 SearchHit(typeCode/moduleCode 메타 동반)로 받아
 *   호출 측이 라우팅(`/:workspace/m/:typeCode/:itemId`)에 사용.
 * - 표시 라벨은 typeLabel/typeLabelI18n + resolveLabel 로 해석(컴포넌트 측).
 *
 * 엔드포인트 미구현(404 등) 시: graceful 처리(빈 결과). 호출 측이 클라 필터 폴백 가능.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api-client";
import type { I18nLabel } from "@/lib/dynamic/types";

/**
 * 통합검색 결과 항목 (범용 메타).
 * 모듈 하드코딩 타입 금지 — typeCode/moduleCode 로 동적 라우팅.
 */
export interface SearchHit {
  /** 아이템 id */
  id: string;
  /** 아이템 표시 번호 */
  itemNo?: string;
  /** 제목 */
  title: string;
  /** 시스템 생성 제목의 4개 언어 맵(없으면 title) */
  titleI18n?: Record<string, string> | null;
  /** 업무유형 코드 (라우팅: /:workspace/m/:typeCode/:id) */
  typeCode: string;
  /** 모듈 코드 (그룹/아이콘 표시용) */
  moduleCode?: string;
  /** 업무유형 표시 라벨(폴백) */
  typeLabel?: string;
  /** 업무유형 다국어 라벨 */
  typeLabelI18n?: I18nLabel;
  /** 일치 컨텍스트 스니펫(서버 제공 시) */
  snippet?: string;
}

/** 검색 결과 응답 (배열 또는 PagedResponse 형태 모두 수용) */
interface SearchEnvelope {
  items?: SearchHit[];
}

/** 통합검색 쿼리 키 (로컬 — query-keys.ts 미소유) */
function searchKey(workspaceSlug: string, q: string, moduleCode: string) {
  return ["workspace", workspaceSlug, "search", q, moduleCode] as const;
}

/**
 * 통합검색(⌘K). q가 비어 있으면 비활성(통합검색). 단, moduleCode 지정 시 빈 q도 허용 —
 * 해당 모듈 아이템 전체(상위 20건)를 반환한다(관련 기록 연결 "메뉴 선택 → 전체 목록").
 * 엔드포인트 부재(404) 시 빈 배열로 graceful 폴백.
 *
 * @param query 검색어
 * @param workspaceSlug 워크스페이스 컨텍스트(헤더)
 * @param moduleCode 모듈 한정(선택) — 지정 시 그 모듈 내로 필터, 빈 q면 전체 목록.
 */
export function useSearch(
  query: string | undefined,
  workspaceSlug: string | undefined,
  moduleCode?: string,
) {
  const q = (query ?? "").trim();
  const mod = (moduleCode ?? "").trim();
  return useQuery({
    queryKey: searchKey(workspaceSlug ?? "", q, mod),
    enabled: Boolean(workspaceSlug && (q.length >= 1 || mod.length >= 1)),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SearchHit[]> => {
      const params = new URLSearchParams();
      params.set("q", q);
      if (mod) params.set("moduleCode", mod);
      try {
        const res = await apiFetch<SearchHit[] | SearchEnvelope>(
          `/api/search?${params.toString()}`,
          { workspaceSlug },
        );
        if (Array.isArray(res)) return res;
        return res.items ?? [];
      } catch (err) {
        // 엔드포인트 미구현/미존재 → 빈 결과(클라 필터 폴백은 호출 측 처리)
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return [];
        }
        throw err;
      }
    },
  });
}
